import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { TablePagination, type PaginationState } from '@/components/shared/TablePagination';
import {
  downloadTemplateJson,
  exportTemplate,
} from '@/modules/admin/templates/lib/export-import.service';
import { Template } from '@/modules/templates/types/templates.types';
import type { LucideIcon } from 'lucide-react';
import {
  Briefcase,
  Building,
  ClipboardList,
  Download,
  FileCheck,
  FilePlus,
  FileSearch,
  FileText,
  Files,
  Folder,
  Handshake,
  Lock,
  MoreVertical,
  Pencil,
  Scale,
  Search,
  Settings2,
  Shield,
  Sparkles,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const iconMap: Record<string, LucideIcon> = {
  FileText,
  Users,
  Lock,
  Shield,
  Sparkles,
  FileCheck,
  FilePlus,
  FileSearch,
  Files,
  Folder,
  Scale,
  Briefcase,
  Building,
  Handshake,
  UserCheck,
  ClipboardList,
};

interface TemplateCardViewProps {
  templates: Template[];
  pagination: PaginationState;
  search: string;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onDelete: (template: Template) => void;
  /** Right-side slot for the search toolbar — page passes the refresh
      button here so it sits next to the search box on mobile. */
  searchToolbarAction?: React.ReactNode;
  loading?: boolean;
}

const formatRelative = (iso: string | undefined, locale: string) => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  const diffMs = Date.now() - date.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHrs < 1) return 'Just now';
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  return date.toLocaleDateString(locale);
};

export function TemplateCardView({
  templates,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onDelete,
  searchToolbarAction,
  loading,
}: TemplateCardViewProps) {
  const locale = useTranslation().i18n.language;
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      {/* Search row — refresh button slides in via searchToolbarAction. */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-globe-grey" />
          <Input
            placeholder={t('ADMIN_TEMPLATES_TABLE_SEARCHPLACEHOLDER')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9 shadow-none"
          />
        </div>
        {searchToolbarAction}
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-globe-grey">
          {search
            ? t('ADMIN_TEMPLATES_TABLE_EMPTY_SEARCH')
            : t('ADMIN_TEMPLATES_TABLE_EMPTY_DEFAULT')}
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => {
            const Icon = iconMap[template?.icon || 'FileText'] || FileText;
            return (
              <article
                key={template.id}
                className="rounded-xl border border-border bg-card p-4 space-y-3"
              >
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-selise-blue/10">
                    <Icon className="size-5 text-selise-blue" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-[hsl(var(--fg))] truncate">
                      {template.title}
                    </h3>
                    <p className="text-sm text-globe-grey line-clamp-1">
                      {template.description ?? '-'}
                    </p>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon-sm" className="-mr-1 shrink-0">
                        <MoreVertical className="size-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-44 p-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start gap-2"
                        asChild
                      >
                        <Link to={`/admin/templates/${template.slug}/edit`}>
                          <Pencil className="size-4" />
                          {t('ADMIN_TEMPLATES_TABLE_ROWACTIONS_EDITDETAILS')}
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start gap-2"
                        asChild
                      >
                        <Link to={`/admin/templates/${template.slug}/builder`}>
                          <Settings2 className="size-4" />
                          {t('ADMIN_TEMPLATES_TABLE_ROWACTIONS_FORMBUILDER')}
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start gap-2"
                        onClick={async () => {
                          try {
                            const data = await exportTemplate(template.slug);
                            if (data) downloadTemplateJson(data);
                          } catch (err) {
                            console.error('Export failed:', err);
                          }
                        }}
                      >
                        <Download className="size-4" />
                        Export
                      </Button>
                      <div className="h-px bg-border my-1" />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => onDelete(template)}
                      >
                        <Trash2 className="size-4" />
                        {t('ADMIN_TEMPLATES_TABLE_ROWACTIONS_DELETE')}
                      </Button>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge
                      variant={template.available ? 'default' : 'secondary'}
                      className={
                        template.available ? 'bg-lime-green text-white hover:bg-lime-green/90' : ''
                      }
                    >
                      {template.available
                        ? t('ADMIN_TEMPLATES_TABLE_BADGES_AVAILABLE')
                        : t('ADMIN_TEMPLATES_TABLE_BADGES_COMINGSOON')}
                    </Badge>
                    {template.popular && (
                      <Badge variant="outline" className="border-selise-blue text-selise-blue">
                        {t('ADMIN_TEMPLATES_TABLE_BADGES_POPULAR')}
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-globe-grey shrink-0">
                    Modified {formatRelative(template.updatedAt, locale)}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pagination.totalPages > 1 && (
        <TablePagination pagination={pagination} onPageChange={onPageChange} />
      )}
    </div>
  );
}
